import React, { FC, useCallback, useState } from "react";
import { Box, Divider, Typography, CircularProgress } from "@mui/material";
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { DeductionConsentFile } from "./models";
import {
  formatFileSize,
  generateDeductionConsentFileName,
  downloadBlobAsFile,
} from "./utils";
import { downloadDeductionConsentFile } from "./api";
import { dynamicNotification } from "src/utils/Notifications";
import { getLanguage } from "src/utils/Languages/LanguageContext";
import __ from "src/utils/Translations";

interface DeductionConsentsExpandedRowProps {
  files: DeductionConsentFile[];
  isLoading?: boolean;
  firstName: string;
  lastName: string;
}

const DeductionConsentsExpandedRow: FC<DeductionConsentsExpandedRowProps> = ({
  files,
  isLoading = false,
  firstName,
  lastName,
}) => {
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const language = getLanguage();

  const handleFileClick = useCallback(
    async (fileId: string, createdAt: string) => {
      if (downloadingFileId) {
        return;
      }

      try {
        setDownloadingFileId(fileId);
        const blob = await downloadDeductionConsentFile(fileId, {
          signal: new AbortController().signal,
        });
        const fileName = generateDeductionConsentFileName(
          createdAt,
          firstName,
          lastName,
          language
        );
        downloadBlobAsFile(blob, fileName);
      } catch (error) {
        dynamicNotification(__("Nie udało się pobrać pliku."), "error");
      } finally {
        setDownloadingFileId(null);
      }
    },
    [downloadingFileId, firstName, language, lastName]
  );

  if (isLoading) {
    return (
      <Box sx={{ p: 2, pl: 9, display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          {__("Pobieranie plików...")}
        </Typography>
      </Box>
    );
  }

  if (!files || files.length === 0) {
    return (
      <Box sx={{ p: 2, pl: 9 }}>
        <Typography variant="body2" color="text.secondary">
          {__("Brak plików zgód na potrącenie")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        p: 2, 
        px: 5, 
        bgcolor: "grey.50",
        borderTop: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {files.map((file, index) => (
          <React.Fragment key={file.id}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 0.5,
              }}
            >
            <UploadFileIcon
              sx={{ fontSize: 20, color: "primary.main" }}
            />
            {downloadingFileId === file.id ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  {__("Pobieranie...")}
                </Typography>
              </Box>
            ) : (
              <Typography
                component="button"
                variant="body2"
                onClick={() => handleFileClick(file.id, file.createdAt)}
                disabled={downloadingFileId !== null}
                sx={{
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                  cursor: downloadingFileId ? "not-allowed" : "pointer",
                  textAlign: "left",
                  border: "none",
                  background: "none",
                  padding: 0,
                  fontFamily: "inherit",
                  fontSize: "inherit",
                  lineHeight: "inherit",
                  opacity: downloadingFileId ? 0.5 : 1,
                }}
              >
                {generateDeductionConsentFileName(
                  file.createdAt,
                  firstName,
                  lastName,
                  language
                )}
              </Typography>
            )}
              {typeof file.fileSize === "number" && (
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mx: 1 }}
                  >
                    •
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatFileSize(file.fileSize)}
                  </Typography>
                </>
              )}
            </Box>
            {index < files.length - 1 && <Divider sx={{ my: 1 }} />}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default DeductionConsentsExpandedRow;

